import { mergeClasses, Tooltip } from "@fluentui/react-components";
import {
  Delete20Regular,
  Edit20Regular,
  Eye20Regular,
  Info16Regular,
} from "@fluentui/react-icons";
import { deleteUseCasePrompt, getCategories } from "api";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import { AdminRowAction } from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { findSidebarItem } from "components/adminSidebar/adminSidebarConfig";
import { useAdminListFilters } from "hooks/useAdminListFilters";
import { useAdminListPagination } from "hooks/useAdminListPagination";
import { useAdminListSearch } from "hooks/useAdminListSearch";
import { useAdminListSort } from "hooks/useAdminListSort";
import { ChevronIcon } from "icons/ChevronIcon";
import { ExportIcon } from "icons/ExportIcon";
import { PlusIcon } from "icons/PlusIcon";
import { SearchIcon } from "icons/SearchIcon";
import { ITableColumn } from "models/ITableColumn";
import { IUseCasePrompt } from "models/IUseCasePrompt";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import {
  useSharedAdminContentHeaderStyles,
  useSharedButtonStyles,
} from "theme/sharedStyles";
import {
  downloadCSV,
  formatDate,
  generateCSV,
  stripHtmlTags,
} from "utils/generalUtils";
import { removeUseCasePrompt } from "../../redux/useCasePromptsSlice";
import { EditUseCasePrompt } from "./editDialog/EditUseCasePrompt";
import { useManageUseCasePromptsStyles } from "./ManageUseCasePromptsListView.styles";
import { ViewUseCasePrompt } from "./viewDialog/ViewUseCasePrompt";

export const ManageUseCasePromptsListView = (): JSX.Element => {
  const cellStyles = useManageUseCasePromptsStyles();
  const colStyles = useAdminColumnStyles();
  const headerClasses = useSharedAdminContentHeaderStyles();
  const buttonStyles = useSharedButtonStyles();
  const dispatch = useDispatch();
  const useCasePrompts = useSelector(
    (state: RootState) => state.useCasePromptsSlice.value,
  );
  const useCases = useSelector((state: RootState) => state.useCasesSlice.value);
  const isLoading = useSelector(
    (state: RootState) => state.useCasePromptsSlice.isLoading,
  );

  const useCaseIdToTitle = useMemo(() => {
    return new Map(useCases.map((uc) => [uc.id, uc.title]));
  }, [useCases]);

  const [tagIdToName, setTagIdToName] = useState<Map<string, string>>(
    new Map(),
  );

  useEffect(() => {
    getCategories(true)
      .then((categories) => {
        const map = new Map<string, string>();
        for (const cat of categories) {
          for (const tag of cat.tags) {
            if (tag.id) map.set(tag.id, tag.name);
          }
        }
        setTagIdToName(map);
      })
      .catch((e) => {
        console.error("Error fetching categories for tags:", e);
      });
  }, []);

  const [isViewPopupOpen, setIsViewPopupOpen] = useState<boolean>(false);
  const [isEditPopupOpen, setIsEditPopupOpen] = useState<boolean>(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState<boolean>(false);
  const [selectedUseCasePrompt, setSelectedUseCasePrompt] =
    useState<IUseCasePrompt | null>(null);
  const { filters, setFilter } = useAdminListFilters({
    cacheKey: "UseCasePrompts",
    filterKeys: ["useCase"] as const,
  });

  const columns: ITableColumn<IUseCasePrompt>[] = useMemo(
    () => [
      {
        columnId: "title",
        displayValue: "Title",
        className: colStyles.normal,
        renderHeaderCell: () => "Title",
        compare: (a: IUseCasePrompt, b: IUseCasePrompt) =>
          a.title.localeCompare(b.title),
        renderCell: (prompt: IUseCasePrompt): JSX.Element => (
          <Tooltip content={prompt.title} relationship="label">
            <strong title={prompt.title}>{prompt.title}</strong>
          </Tooltip>
        ),
      },
      {
        columnId: "description",
        displayValue: "Description",
        className: colStyles.wide,
        renderHeaderCell: () => "Description",
        compare: (a: IUseCasePrompt, b: IUseCasePrompt) =>
          a.description.localeCompare(b.description),
        renderCell: (prompt: IUseCasePrompt): JSX.Element => (
          <Tooltip content={prompt.description} relationship="label">
            <div
              className={cellStyles.truncatedText}
              title={prompt.description}
            >
              {prompt.description}
            </div>
          </Tooltip>
        ),
      },
      {
        columnId: "updatedAt",
        displayValue: "Last Updated",
        className: colStyles.normal,
        renderHeaderCell: () => "Last Updated",
        compare: (a: IUseCasePrompt, b: IUseCasePrompt) =>
          a.updatedAt - b.updatedAt,
        renderCell: (prompt: IUseCasePrompt): JSX.Element => (
          <span>{formatDate(prompt.updatedAt)}</span>
        ),
      },
      {
        columnId: "useCaseIds",
        displayValue: "Use Case",
        className: colStyles.normal,
        isSortable: false,
        renderHeaderCell: () => "Use Case",
        compare: () => 0,
        renderCell: (prompt: IUseCasePrompt): JSX.Element | null => {
          if (!prompt.useCaseIds || prompt.useCaseIds.length === 0) {
            return null;
          }
          const allNames = prompt.useCaseIds
            .map((id) => useCaseIdToTitle.get(id))
            .filter(Boolean);
          if (allNames.length === 0) {
            return null;
          }
          const firstName = allNames[0];
          const remaining = allNames.length - 1;
          const tooltipText = allNames.map((n) => `• ${n}`).join("\n");
          return (
            <Tooltip
              content={
                <span className={cellStyles.tooltipContent}>{tooltipText}</span>
              }
              relationship="description"
              positioning="above"
            >
              <div className={cellStyles.useCaseCell}>
                <span>{firstName}</span>
                {remaining > 0 && (
                  <span className={cellStyles.useCaseMoreText}>
                    +{remaining} more
                  </span>
                )}
              </div>
            </Tooltip>
          );
        },
      },
      {
        columnId: "owner",
        displayValue: "Owner",
        className: colStyles.normal,
        renderHeaderCell: () => (
          <div className={cellStyles.ownerHeader}>
            <span>Owner</span>
            <Tooltip
              content="The SME for the Use Case Prompt"
              relationship="label"
            >
              <Info16Regular className={cellStyles.infoIcon} />
            </Tooltip>
          </div>
        ),
        compare: (a: IUseCasePrompt, b: IUseCasePrompt) => {
          const aName = a.owner?.displayName || "";
          const bName = b.owner?.displayName || "";
          return aName.localeCompare(bName);
        },
        renderCell: (prompt: IUseCasePrompt): JSX.Element => (
          <Tooltip
            content={prompt.owner?.displayName || "-"}
            relationship="label"
          >
            <span title={prompt.owner?.displayName}>
              {prompt.owner?.displayName || "-"}
            </span>
          </Tooltip>
        ),
      },
    ],
    [cellStyles, colStyles, useCaseIdToTitle],
  );

  const {
    sortColumn,
    isSortDescending,
    sortItems,
    onSortColumnChanged,
    onClearColumnChanged,
  } = useAdminListSort<IUseCasePrompt>({
    cacheKey: "useCasesPrompts",
    defaultSortColumn: "updatedAt",
    defaultDescending: true,
  });

  const searchFields = useMemo(
    () => ["title", "description"] as (keyof IUseCasePrompt)[],
    [],
  );
  const {
    searchQuery: internalSearchQuery,
    handleSearchChange,
    handleSearchValueChange,
    filteredItems,
  } = useAdminListSearch<IUseCasePrompt>({
    items: useCasePrompts,
    searchFields,
  });

  const useCaseOptions = useMemo(
    () => useCases.map((uc) => ({ key: uc.id ?? "", text: uc.title })),
    [useCases],
  );

  const fullyFilteredItems = useMemo(() => {
    let result = filteredItems;
    if (filters.useCase)
      result = result.filter((p) => p.useCaseIds?.includes(filters.useCase));
    return result;
  }, [filteredItems, filters.useCase]);

  const sortedItems = useMemo(
    () => sortItems(fullyFilteredItems, columns),
    [fullyFilteredItems, sortItems, columns],
  );

  const emptyStateMessage = useMemo(() => {
    const hasActiveFilters = internalSearchQuery || filters.useCase;
    return hasActiveFilters
      ? "No prompts match the current filters."
      : "No prompts found";
  }, [internalSearchQuery, filters.useCase]);

  const {
    paginatedItems,
    selectedPageIndex,
    pageCount,
    handlePageChange,
    totalItemsCount,
    itemsPerPage,
  } = useAdminListPagination<IUseCasePrompt>({
    items: sortedItems,
    itemsPerPage: 10,
  });

  const handleViewClick = (prompt: IUseCasePrompt): void => {
    setSelectedUseCasePrompt(prompt);
    setIsViewPopupOpen(true);
  };

  const handleEditClick = (prompt: IUseCasePrompt): void => {
    setSelectedUseCasePrompt(prompt);
    setIsEditPopupOpen(true);
  };

  const handleDeleteClick = (prompt: IUseCasePrompt): void => {
    setSelectedUseCasePrompt(prompt);
    setShowDeleteDialog(true);
  };

  const handleCreateClick = (): void => {
    setSelectedUseCasePrompt(null);
    setIsEditPopupOpen(true);
  };

  const handleViewDismiss = (): void => {
    setIsViewPopupOpen(false);
    setSelectedUseCasePrompt(null);
  };

  const handleEditDismiss = (isSaved: boolean): void => {
    setIsEditPopupOpen(false);
    if (isSaved) {
      setSelectedUseCasePrompt(null);
    }
  };

  const handleDeleteDismiss = (): void => {
    setShowDeleteDialog(false);
  };

  const handleDeleteConfirmed = async (): Promise<void> => {
    if (!selectedUseCasePrompt?.id) return;

    try {
      await deleteUseCasePrompt({ promptId: selectedUseCasePrompt.id });
      dispatch(removeUseCasePrompt(selectedUseCasePrompt.id));
      setSelectedUseCasePrompt(null);
    } catch (error) {
      console.error("Error deleting useCasePrompt:", error);
    } finally {
      setShowDeleteDialog(false);
    }
  };

  const rowActions: AdminRowAction<IUseCasePrompt>[] = useMemo(
    () => [
      { icon: <Eye20Regular />, tooltip: "View", onClick: handleViewClick },
      { icon: <Edit20Regular />, tooltip: "Edit", onClick: handleEditClick },
      {
        icon: <Delete20Regular />,
        tooltip: "Delete",
        onClick: handleDeleteClick,
      },
    ],
    [],
  );

  const sidebarItem = useMemo(() => findSidebarItem("useCasePrompts"), []);

  const handleExport = useCallback((): void => {
    const csv = generateCSV(fullyFilteredItems, [
      { header: "Title", accessor: (p) => stripHtmlTags(p.title) },
      { header: "Description", accessor: (p) => stripHtmlTags(p.description) },
      { header: "Prompt", accessor: (p) => stripHtmlTags(p.prompt) },
      {
        header: "Use Cases",
        accessor: (p) =>
          (p.useCaseIds || [])
            .map((id) => useCaseIdToTitle.get(id))
            .filter(Boolean)
            .join("; "),
      },
      { header: "Owner", accessor: (p) => p.owner?.displayName || "" },
      { header: "Author", accessor: (p) => p.user?.displayName || "" },
      {
        header: "Quals Enabled",
        accessor: (p) => (p.qualsEnabled ? "Yes" : "No"),
      },
      {
        header: "Proposals Enabled",
        accessor: (p) => (p.proposalsEnabled ? "Yes" : "No"),
      },
      {
        header: "Citations Enabled",
        accessor: (p) => (p.citationsEnabled ? "Yes" : "No"),
      },
      { header: "LLM Model", accessor: (p) => p.llmModel || "" },
      {
        header: "KC",
        accessor: (p) =>
          (p.preselectedKnowledgeComponents || [])
            .map((id) => tagIdToName.get(id) ?? id)
            .join("; "),
      },
      { header: "Last Updated", accessor: (p) => formatDate(p.updatedAt) },
    ]);
    downloadCSV(csv, "use-case-prompts.csv");
  }, [fullyFilteredItems, useCaseIdToTitle, tagIdToName]);

  return (
    <>
      <div className={headerClasses.root}>
        <div
          className={mergeClasses(
            headerClasses.titleRow,
            headerClasses.titleRowWithFilters,
          )}
        >
          <h1 className={headerClasses.title}>
            {sidebarItem?.label || "Use Case Prompts"}
          </h1>
          <div className={headerClasses.actions}>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.secondaryButton,
              )}
              onClick={handleExport}
              type="button"
            >
              <ExportIcon /> Export
            </button>
            <button
              className={mergeClasses(
                headerClasses.actionButton,
                buttonStyles.primaryButton,
              )}
              onClick={handleCreateClick}
              type="button"
            >
              <PlusIcon /> Add New
            </button>
          </div>
        </div>
        <div className={headerClasses.filterRow}>
          <div className={headerClasses.searchInputWrapper}>
            <SearchIcon className={headerClasses.searchIcon} />
            <input
              type="text"
              className={headerClasses.searchInput}
              placeholder="Search title or description..."
              value={internalSearchQuery}
              onChange={(e) => handleSearchValueChange(e.target.value)}
              aria-label="Search title or description..."
            />
          </div>
          <div className={headerClasses.filterSelectWrapper}>
            <select
              className={headerClasses.filterSelect}
              value={filters.useCase}
              onChange={(e) => setFilter("useCase", e.target.value)}
              aria-label="All Use Cases"
            >
              <option value="">All Use Cases</option>
              {useCaseOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>
                  {opt.text}
                </option>
              ))}
            </select>
            <ChevronIcon className={headerClasses.filterChevron} />
          </div>
        </div>
      </div>
      <AdminListPage<IUseCasePrompt>
        title="Use Case Prompts"
        items={useCasePrompts}
        columns={columns}
        isLoading={isLoading}
        headerActions={[]}
        searchPlaceholder="Search title or description..."
        searchQuery={internalSearchQuery}
        onSearchChange={handleSearchChange}
        hideHeader={true}
        rowActions={rowActions}
        getRowId={(prompt) => prompt.id!}
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortColumnChanged}
        onSortClear={onClearColumnChanged}
        paginatedItems={paginatedItems}
        selectedPageIndex={selectedPageIndex}
        pageCount={pageCount}
        totalItemsCount={totalItemsCount}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
        emptyStateMessage={emptyStateMessage}
      />

      {isEditPopupOpen && (
        <EditUseCasePrompt
          isOpen={isEditPopupOpen}
          onDismiss={handleEditDismiss}
          useCasePrompt={selectedUseCasePrompt}
        />
      )}

      {isViewPopupOpen && selectedUseCasePrompt && (
        <ViewUseCasePrompt
          isOpen={isViewPopupOpen}
          onDismiss={handleViewDismiss}
          useCasePrompt={selectedUseCasePrompt}
        />
      )}

      <ActionDialog
        isVisible={showDeleteDialog}
        title="Delete Prompt"
        message={`Are you sure you want to permanently delete "${selectedUseCasePrompt?.title}"? This action cannot be undone.`}
        confirmationMessage="Delete"
        dismissMessage="Cancel"
        onConfirmation={handleDeleteConfirmed}
        onDismiss={handleDeleteDismiss}
      />
    </>
  );
};
